/**
 * Périmètre de l'espace Pilot — UN choix, dans l'en-tête, pour tous les écrans
 * (spec/pilot-cible.md, règle 2). Compte (internes, consultant) et équipe (ORG).
 *
 * Persisté dans la même clé que la vue du plan (`bizcoach_plan_view`) : le plan
 * lisait déjà `account` et `scope` là ; on ne fait que remonter la source d'un
 * niveau. Un écran qui lit le périmètre s'abonne (`usePilotScope`) ; les appels
 * réseau l'ajoutent à l'URL (`withPilotScope`).
 */
import { useSyncExternalStore } from "react";

export const PILOT_SCOPE_KEY = "bizcoach_plan_view";
const EVENT = "bizcoach:pilot-scope";

export interface PilotScope { account: string | null; scope: string | null }

function read(): PilotScope {
  try {
    const raw = JSON.parse(localStorage.getItem(PILOT_SCOPE_KEY) || "{}") as Record<string, unknown>;
    return { account: typeof raw.account === "string" ? raw.account : null, scope: typeof raw.scope === "string" ? raw.scope : null };
  } catch { return { account: null, scope: null }; }
}

let cache: PilotScope = read();
let cacheKey = JSON.stringify(cache);

export function getPilotScope(): PilotScope { return cache; }

export function setPilotScope(next: Partial<PilotScope>) {
  try {
    const raw = JSON.parse(localStorage.getItem(PILOT_SCOPE_KEY) || "{}") as Record<string, unknown>;
    const merged = { ...raw, ...next };
    localStorage.setItem(PILOT_SCOPE_KEY, JSON.stringify(merged));
  } catch { /* stockage indisponible : l'état mémoire suffit pour la session */ }
  const n = { account: next.account !== undefined ? next.account : cache.account, scope: next.scope !== undefined ? next.scope : cache.scope };
  const k = JSON.stringify(n);
  if (k === cacheKey) return;
  cache = n; cacheKey = k;
  try { window.dispatchEvent(new Event(EVENT)); } catch { /* noop */ }
}

function subscribe(cb: () => void) {
  const onChange = () => { const n = read(); const k = JSON.stringify(n); if (k !== cacheKey) { cache = n; cacheKey = k; } cb(); };
  window.addEventListener(EVENT, onChange);
  window.addEventListener("storage", onChange);
  return () => { window.removeEventListener(EVENT, onChange); window.removeEventListener("storage", onChange); };
}

export function usePilotScope(): PilotScope {
  return useSyncExternalStore(subscribe, getPilotScope, getPilotScope);
}

/** Ajoute `accountId` et `scope` du périmètre courant à une URL d'API. */
export function withPilotScope(url: string): string {
  const { account, scope } = cache;
  if (!account && !scope) return url;
  const u = new URL(url, "http://x");
  if (account && !u.searchParams.has("accountId")) u.searchParams.set("accountId", account);
  if (scope && !u.searchParams.has("scope")) u.searchParams.set("scope", scope);
  return u.pathname + u.search;
}
